/**
 * The refusal a route answers when Apple 5.1.2(i)'s permission is still owed
 * (CMP-01).
 *
 * A REFUSAL IS AN EXPECTED ANSWER, NOT AN ERROR - the rule the entitlement and
 * rate-limit refusals already state, and it applies here word for word. No
 * error logger, no error tracker, no cold-failure state. A learner who has not
 * been asked yet is not a fault.
 *
 * ITS OWN CODE, not folded into the entitlement or capability refusals, because
 * the learner's action is different for each. A tier refusal says buy
 * something; a capability refusal says come back later; this one says answer a
 * question that is already on screen. A client that could not tell them apart
 * would show the wrong sentence, which is the exact failure 42-09 recorded when
 * a refusal reached the client and no surface knew about it.
 *
 * 403 RATHER THAN 401. The learner IS authenticated - the whole point is that
 * we know who they are and have not yet been given permission on their behalf.
 * A 401 would send both clients into their session-expired path and sign
 * somebody out for not having ticked a box.
 */

export const AI_CONSENT_REQUIRED_CODE = "AI_CONSENT_REQUIRED";

/** The copy key both clients spend, so the two say the same sentence. */
export const AI_CONSENT_REQUIRED_REASON_KEY = "aiConsent.required";

export type AiConsentRefusalBody = {
  error: string;
  code: typeof AI_CONSENT_REQUIRED_CODE;
  /** What the client must show; it does not have to look the key up itself. */
  reasonKey: typeof AI_CONSENT_REQUIRED_REASON_KEY;
};

export function aiConsentRequiredBody(): AiConsentRefusalBody {
  return {
    error: "AI processing consent is required.",
    code: AI_CONSENT_REQUIRED_CODE,
    reasonKey: AI_CONSENT_REQUIRED_REASON_KEY,
  };
}

/**
 * Discriminates by SHAPE, so a client can recognise the refusal from a parsed
 * envelope without importing anything across the bundle boundary.
 */
export function isAiConsentRefusal(value: unknown): value is AiConsentRefusalBody {
  if (typeof value !== "object" || value === null) return false;
  return (value as { code?: unknown }).code === AI_CONSENT_REQUIRED_CODE;
}
